import emojiRegex from "emoji-regex";

import { MarkdownProps } from "..";
import {
  UNICODE_EMOJI_MAX_PACK,
  UNICODE_EMOJI_MIN_PACK,
} from "./UnicodeEmoji";

/**
 * Regex for custom emoji
 */
export const RE_CUSTOM_EMOJI = /:([0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}):/g;

/**
 * Regex for any emoji
 */
export const RE_ANY_EMOJI = new RegExp(
  RE_CUSTOM_EMOJI.source + "|[\uE0E0-\uE0E6]?(?:" + emojiRegex().source + ")",
  "g",
);

/**
 * Check if a piece of text is only comprised of emoji
 * @param text Text
 * @returns Whether it is only emoji
 */
export function isOnlyEmoji(text: string) {
  return text.replaceAll(RE_ANY_EMOJI, "").trim().length === 0;
}

/**
 * Strip the private-use unicode pack selector used for rendering emoji packs.
 * Reactions should send the raw unicode emoji to the backend.
 */
export function stripUnicodeEmojiSelector(text: string) {
  if (!text) return text;

  const firstCodePoint = text.codePointAt(0);
  if (
    firstCodePoint &&
    firstCodePoint >= UNICODE_EMOJI_MIN_PACK &&
    firstCodePoint <= UNICODE_EMOJI_MAX_PACK
  ) {
    return text.substring(1);
  }

  return text;
}

function emojiDisplaySize(text: string) {
  if (!isOnlyEmoji(text)) return;

  RE_ANY_EMOJI.lastIndex = 0;
  RE_ANY_EMOJI.exec(text);

  let size = "large";
  if (RE_ANY_EMOJI.exec(text)) {
    size = "medium";
  }

  return size;
}

function extractText(node: unknown): string {
  if (!node || typeof node !== "object") return "";

  const current = node as {
    type?: string;
    value?: string;
    tagName?: string;
    properties?: Record<string, string>;
    children?: unknown[];
  };

  if (current.type === "text") {
    return current.value ?? "";
  }

  if (current.tagName === "unicodeEmoji") {
    return current.properties?.str ?? "";
  }

  if (current.tagName === "customEmoji") {
    const id = current.properties?.emoji ?? current.properties?.id;
    return id ? `:${id}:` : "";
  }

  return current.children?.map(extractText).join("") ?? "";
}

function applyEmojiSizeToChildren(node: unknown, size: string) {
  if (!node || typeof node !== "object") {
    return;
  }

  const current = node as {
    tagName?: string;
    properties?: Record<string, unknown>;
    children?: unknown[];
  };

  if (
    (current.tagName === "unicodeEmoji" || current.tagName === "customEmoji") &&
    current.properties
  ) {
    const cssSize = size === "large" ? "5.6em" : "2.8em";
    const existingStyle =
      typeof current.properties.style === "string"
        ? current.properties.style.trim()
        : "";

    current.properties.style = [
      existingStyle,
      `--emoji-size: ${cssSize}`,
      `width: ${cssSize}`,
      `height: ${cssSize}`,
      "margin: 0",
      "vertical-align: middle",
    ]
      .filter(Boolean)
      .join("; ");
  }

  for (const child of current.children ?? []) {
    applyEmojiSizeToChildren(child, size);
  }
}

/**
 * Inject emoji size information into a hast node
 * @param props Pass-through Markdown props
 * @param hastNode Target hast node
 */
export function injectEmojiSize(
  props: MarkdownProps,
  hastNode: { children?: unknown[] },
) {
  if (props.disallowBigEmoji) {
    return;
  }

  const visit = (node: unknown) => {
    if (!node || typeof node !== "object") {
      return;
    }

    const current = node as {
      tagName?: string;
      properties?: Record<string, string>;
      children?: unknown[];
    };

    if (current.tagName === "p" && current.properties) {
      const size = emojiDisplaySize(extractText(current));
      if (size) {
        const existingStyle =
          typeof current.properties.style === "string"
            ? current.properties.style.trim()
            : "";

        current.properties.style = [existingStyle, "line-height: 1"]
          .filter(Boolean)
          .join("; ");

        applyEmojiSizeToChildren(current, size);
      }
    }

    for (const child of current.children ?? []) {
      visit(child);
    }
  };

  visit(hastNode);
}

/**
 * Function provided under the MIT License
 * Copyright (c) 2016-20 Ionică Bizău <bizauionica@gmail.com> (https://ionicabizau.net)
 * https://github.com/IonicaBizau/emoji-unicode/blob/master/LICENSE
 */
export function toCodepoint(input: string) {
  if (input.length === 1) {
    return input.charCodeAt(0).toString(16);
  } else if (input.length > 1) {
    const pairs = [];
    for (let i = 0; i < input.length; i++) {
      if (
        // high surrogate
        input.charCodeAt(i) >= 0xd800 &&
        input.charCodeAt(i) <= 0xdbff
      ) {
        if (
          input.charCodeAt(i + 1) >= 0xdc00 &&
          input.charCodeAt(i + 1) <= 0xdfff
        ) {
          // low surrogate
          pairs.push(
            (input.charCodeAt(i) - 0xd800) * 0x400 +
              (input.charCodeAt(i + 1) - 0xdc00) +
              0x10000,
          );
        }
      } else if (input.charCodeAt(i) < 0xd800 || input.charCodeAt(i) > 0xdfff) {
        // modifiers and joiners
        pairs.push(input.charCodeAt(i));
      }
    }

    return pairs.map((char) => char.toString(16)).join("-");
  }

  return "";
}
